import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { vendorsService } from '../../services/payables/vendors';
import { FaTimes, FaHistory, FaSearch, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa';
import LoadingSpinner from '../ui/LoadingSpinner';
import { formatDate, formatCurrency } from '../../utils/formatting';

const VendorPaymentHistoryModal = ({ vendor, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalAmount: 0,
    lastPaymentDate: null,
    averagePayment: 0
  });

  useEffect(() => {
    fetchPaymentHistory();
  }, [vendor.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const paymentData = await vendorsService.getVendorPaymentHistory(user.companyId, vendor.id);
      setPayments(paymentData);
      calculateSummary(paymentData);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError(t('common.error_loading_data'));
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (paymentData) => {
    const summary = {
      totalPayments: paymentData.length,
      totalAmount: paymentData.reduce((sum, payment) => sum + (payment.amount || 0), 0),
      lastPaymentDate: paymentData.length > 0 ? paymentData[0].paymentDate : null,
      averagePayment: paymentData.length > 0 ? paymentData.reduce((sum, payment) => sum + (payment.amount || 0), 0) / paymentData.length : 0
    };
    setSummary(summary);
  };





  const getPaymentMethodLabel = (method) => {
    const methodMap = {
      'cash': t('accounting.cash'),
      'check': t('accounting.check'),
      'bank_transfer': t('accounting.bank_transfer'),
      'credit_card': t('accounting.credit_card'),
      'online_payment': t('accounting.online_payment')
    };
    return methodMap[method] || method;
  };

  const filterPayments = () => {
    let filtered = payments;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(payment => 
        payment.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.billNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'this_month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'last_3_months':
          filterDate.setMonth(now.getMonth() - 3);
          break;
        case 'last_6_months':
          filterDate.setMonth(now.getMonth() - 6);
          break;
        case 'this_year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }
      
      filtered = filtered.filter(payment => {
        const paymentDate = payment.paymentDate?.toDate ? payment.paymentDate.toDate() : new Date(payment.paymentDate);
        return paymentDate >= filterDate;
      });
    }

    return filtered.sort((a, b) => {
      const dateA = a.paymentDate?.toDate?.() || new Date(a.paymentDate || 0);
      const dateB = b.paymentDate?.toDate?.() || new Date(b.paymentDate || 0);
      return dateB - dateA;
    });
  };

  const filteredPayments = filterPayments();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FaHistory className="w-5 h-5 text-blue-600" />
              {t('vendors.last_payment')}
            </h2>
            <p className="text-gray-600 mt-1">
              {vendor.name || vendor.companyName} - {t('accounting.vendor_payments')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="p-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('accounting.total_payments')}</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.totalPayments}</p>
                </div>
                <FaMoneyBillWave className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('quotations.total')}</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalAmount)}</p>
                </div>
                <FaMoneyBillWave className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('common.average_payment')}</p>
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.averagePayment)}</p>
                </div>
                <FaMoneyBillWave className="w-8 h-8 text-orange-600" />
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('common.last_payment')}</p>
                  <p className="text-lg font-bold text-purple-600">{formatDate(summary.lastPaymentDate)}</p>
                </div>
                <FaCalendarAlt className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('common.all_dates')}</option>
              <option value="this_month">{t('common.this_month')}</option>
              <option value="last_3_months">{t('common.last_3_months')}</option>
              <option value="last_6_months">{t('common.last_6_months')}</option>
              <option value="this_year">{t('common.this_year')}</option>
            </select>
          </div>
        </div>

        {/* Payment History Table */}
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left p-4 font-medium text-gray-900">{t('accounting.payment_date')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('accounting.payment_amount')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('accounting.payment_method')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('bill_payments.billDate')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('common.description')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('accounting.payment_reference')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="border-b hover:bg-gray-50">
                  <td className="p-4 text-gray-900">{formatDate(payment.paymentDate)}</td>
                  <td className="p-4">
                    <span className="font-medium text-green-600">
                      {formatCurrency(payment.amount)}
                    </span>
                  </td>
                  <td className="p-4 text-gray-900">
                    {getPaymentMethodLabel(payment.paymentMethod)}
                  </td>
                  <td className="p-4 text-gray-900">{payment.billNumber || '-'}</td>
                  <td className="p-4 text-gray-900">{payment.description || '-'}</td>
                  <td className="p-4 text-gray-900">{payment.reference || '-'}</td>
                  <td className="p-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      payment.status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : payment.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {payment.status === 'completed' ? t('accounting.completed') : 
                       payment.status === 'pending' ? t('common.pending') : 
                       t('common.failed')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredPayments.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {payments.length === 0 ? t('common.no_payments_found') : t('common.no_matching_results')}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-6 bg-red-50 border border-red-200 rounded-lg m-6">
            <div className="flex">
              <div className="text-red-600 text-sm">{error}</div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorPaymentHistoryModal; 